import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalibrationConfig, ScoringEvent, DetectedBlob } from '../types';
import { CameraView } from './CameraView';
import { BallDetector } from '../utils/ballDetection';
import { apiClient } from '../services/api';
import { logCapture } from '../utils/logCapture';

interface ScoringViewProps {
  config: CalibrationConfig;
  onRecalibrate: () => void;
}

export function ScoringView({ config, onRecalibrate }: ScoringViewProps) {
  const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [events, setEvents] = useState<ScoringEvent[]>([]);
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectedBlobs, setDetectedBlobs] = useState<DetectedBlob[]>([]);
  const [ballScores, setBallScores] = useState<number[]>([]); // Track individual ball scores
  const [isCanvasReady, setIsCanvasReady] = useState(false);
  const [gameId, setGameId] = useState<string | null>(null);
  const [gameStatus, setGameStatus] = useState<'not_started' | 'active' | 'completed'>('not_started');

  const detectorRef = useRef<BallDetector>(new BallDetector());
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const animationFrameRef = useRef<number>();
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Start log capture when component mounts
  useEffect(() => {
    logCapture.start();
    console.log('[ScoringView] Component mounted, log capture started');

    return () => {
      logCapture.stop();
    };
  }, []);

  // Don't auto-start detection - wait for game to start
  useEffect(() => {
    // Detection will start when game is created
  }, []);

  const handleCanvasReady = useCallback((canvas: HTMLCanvasElement, video: HTMLVideoElement) => {
    canvasRef.current = canvas;
    videoRef.current = video;

    // Create overlay canvas for detection visualization
    const overlayCanvas = document.createElement('canvas');
    overlayCanvas.width = canvas.width;
    overlayCanvas.height = canvas.height;
    overlayCanvasRef.current = overlayCanvas;

    // Signal that canvas is ready - this will trigger the detection loop
    setIsCanvasReady(true);
  }, []);

  const startDetection = () => {
    setIsDetecting(true);
    detectorRef.current.reset();
  };

  const stopDetection = () => {
    setIsDetecting(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  };

  const createNewGame = async () => {
    try {
      console.log('[ScoringView] Creating new game...');
      const response = await apiClient.createGame({
        detectionConfig: {
          motionThreshold: 30,
          minBlobArea: 200,
          maxBlobArea: 5000,
        },
      });

      setGameId(response.gameId);
      setGameStatus('active');
      console.log('[ScoringView] Game created:', response.gameId);

      // Clear log capture for new game
      logCapture.clear();

      // Start detection when game is created
      setIsDetecting(true);
    } catch (error) {
      console.error('[ScoringView] Failed to create game:', error);
    }
  };

  const endGame = async () => {
    if (!gameId) return;

    // Prevent duplicate calls
    if (gameStatus === 'completed') {
      console.log('[ScoringView] Game already ended, skipping duplicate call');
      return;
    }

    try {
      console.log('[ScoringView] Ending game:', gameId);

      // Mark as completed immediately to prevent duplicate calls
      setGameStatus('completed');

      // Upload logs to backend
      const allLogs = logCapture.getLogs();
      // Map to backend format (exclude args field)
      const logs = allLogs.map(log => ({
        timestamp: log.timestamp,
        level: log.level,
        message: log.message,
      }));
      await apiClient.uploadLogs(gameId, { logs });
      console.log('[ScoringView] Logs uploaded successfully');

      // Don't reset display - let user see final score
      // Display will be reset when "New Game" is clicked
    } catch (error) {
      console.error('[ScoringView] Failed to end game:', error);
    }
  };

  const resetScore = async () => {
    // End current game if active
    if (gameId && gameStatus === 'active') {
      await endGame();
    }

    // Stop detection
    setIsDetecting(false);

    // Reset UI state
    setScore(0);
    setEvents([]);
    setBallScores([]);
    setGameId(null);
    setGameStatus('not_started');
    detectorRef.current.reset();
    logCapture.clear();

    // Create new game (this will start detection)
    await createNewGame();
  };

  useEffect(() => {
    if (!isDetecting || !isCanvasReady || !canvasRef.current || !videoRef.current) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const detector = detectorRef.current;

    console.log('[ScoringView] Starting detection loop');

    async function detectLoop() {
      if (!isDetecting || !canvas || !video) return;

      const { blobs, events: newEvents } = detector.detect(video, canvas, config.holes, config.startZone, config.gutterZone);

      setDetectedBlobs(blobs);

      if (newEvents.length > 0) {
        setEvents(prev => [...newEvents, ...prev]);
        const scoreIncrease = newEvents.reduce((sum, e) => sum + e.holeValue, 0);
        setScore(prev => prev + scoreIncrease);

        // Add each new event's score to the ball scores array and send to backend
        for (const event of newEvents) {
          // Use the detector's ball ID directly (Ball 0, Ball 1, etc.)
          const ballId = event.ballId;

          // Update ball scores
          setBallScores(prev => [...prev, event.holeValue]);

          // Send event to backend
          if (gameId) {
            try {
              await apiClient.submitEvent(gameId, {
                timestamp: event.timestamp,
                ballId,
                holeValue: event.holeValue,
                confidence: event.confidence,
                position: {
                  x: event.x,
                  y: event.y,
                },
              });
              console.log(`[ScoringView] Event submitted for ball ${ballId}`);
            } catch (error) {
              console.error('[ScoringView] Failed to submit event:', error);
            }
          }
        }

        // Auto-end game after 9th ball (check after all events processed)
        if (ballScores.length + newEvents.length >= 9 && gameStatus === 'active') {
          console.log('[ScoringView] 9 balls scored, ending game automatically');
          setTimeout(() => endGame(), 1000); // Delay to ensure state updates
        }
      }

      animationFrameRef.current = requestAnimationFrame(detectLoop);
    }

    detectLoop();

    return () => {
      console.log('[ScoringView] Stopping detection loop');
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isDetecting, isCanvasReady, config.holes, config.startZone, config.gutterZone, gameId]);

  // Draw detection overlay
  useEffect(() => {
    if (!overlayCanvasRef.current) return;

    const ctx = overlayCanvasRef.current.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, overlayCanvasRef.current.width, overlayCanvasRef.current.height);

    // Draw detected blobs
    detectedBlobs.forEach(blob => {
      ctx.beginPath();
      ctx.arc(blob.x, blob.y, blob.radius, 0, 2 * Math.PI);
      ctx.strokeStyle = '#FF00FF';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw centroid
      ctx.fillStyle = '#FF00FF';
      ctx.fillRect(blob.x - 3, blob.y - 3, 6, 6);
    });
  }, [detectedBlobs]);

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <h1 style={{ marginBottom: '10px' }}>SkeeScore - Live Scoring</h1>

        {/* Ball Scores Display Row */}
        <div style={{
          marginBottom: '15px',
          padding: '15px',
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}>
          {/* Ball scores 1-9 */}
          {Array.from({ length: 9 }, (_, i) => {
            const ballNum = i + 1;
            const score = ballScores[i];
            const hasScore = score !== undefined;

            return (
              <div
                key={ballNum}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: '60px',
                }}
              >
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '4px',
                }}>
                  Ball {ballNum}
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: hasScore ? '#4CAF50' : '#ccc',
                  backgroundColor: hasScore ? '#e8f5e9' : '#f5f5f5',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  minWidth: '50px',
                  textAlign: 'center',
                }}>
                  {hasScore ? score : '-'}
                </div>
              </div>
            );
          })}

          {/* Total Score */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: '80px',
            marginLeft: '20px',
            paddingLeft: '20px',
            borderLeft: '2px solid #ddd',
          }}>
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginBottom: '4px',
            }}>
              TOTAL
            </div>
            <div style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#2196F3',
              backgroundColor: '#e3f2fd',
              padding: '8px 16px',
              borderRadius: '4px',
              minWidth: '70px',
              textAlign: 'center',
            }}>
              {score}
            </div>
          </div>

          {/* Game Control Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '8px',
            marginLeft: '20px',
            paddingLeft: '20px',
            borderLeft: '2px solid #ddd',
          }}>
            <button onClick={resetScore} style={{
              ...buttonStyle(gameStatus === 'not_started' ? '#4CAF50' : '#FF9800'),
              margin: 0,
              padding: gameStatus === 'not_started' ? '16px 32px' : '12px 24px',
              fontSize: gameStatus === 'not_started' ? '18px' : '16px',
              fontWeight: gameStatus === 'not_started' ? 'bold' : 'normal',
            }}>
              {gameStatus === 'not_started' ? 'START NEW GAME' : gameStatus === 'completed' ? 'NEW GAME' : 'New Game'}
            </button>
            {gameStatus === 'active' && (
              <button onClick={endGame} style={{
                ...buttonStyle('#f44336'),
                margin: 0,
                padding: '8px 16px',
                fontSize: '14px',
              }}>
                End Game
              </button>
            )}
          </div>
        </div>
        </div>

        <div style={{ position: 'relative' }}>
          <CameraView
            holes={config.holes}
            startZone={config.startZone}
            gutterZone={config.gutterZone}
            onCanvasReady={handleCanvasReady}
          />

          {/* Overlay when game not started */}
          {gameStatus === 'not_started' && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '32px',
              fontWeight: 'bold',
              textAlign: 'center',
              padding: '20px',
              zIndex: 10,
            }}>
              <div style={{ marginBottom: '20px' }}>🎯</div>
              <div>Click "START NEW GAME" to Begin</div>
              <div style={{ fontSize: '18px', fontWeight: 'normal', marginTop: '10px', color: '#ccc' }}>
                Detection is paused until game starts
              </div>
            </div>
          )}
        </div>

        {/* Detection Status Line */}
        <div style={{ fontSize: '14px', color: '#666', marginTop: '15px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span>Game: {gameStatus === 'not_started' ? '⚪ Not Started' : gameStatus === 'active' ? '🟢 Active' : '🔴 Completed'}</span>
          {gameId && <span style={{ fontSize: '12px', color: '#999' }}>ID: {gameId.substring(0, 8)}...</span>}
          <span style={{ marginLeft: '10px' }}>Detection: {isDetecting ? '🟢 Active' : '🔴 Stopped'}</span>
          {!isDetecting ? (
            <button onClick={startDetection} style={{ ...buttonStyle('#4CAF50'), padding: '4px 12px', fontSize: '12px', margin: 0 }}>
              Start Detection
            </button>
          ) : (
            <button onClick={stopDetection} style={{ ...buttonStyle('#f44336'), padding: '4px 12px', fontSize: '12px', margin: 0 }}>
              Stop Detection
            </button>
          )}
          <span style={{ marginLeft: '10px' }}>Areas: {config.holes.length}</span>
          <span>Blobs: {detectedBlobs.length}</span>
          <button onClick={onRecalibrate} style={{ ...buttonStyle('#2196F3'), padding: '4px 12px', fontSize: '12px', margin: 0, marginLeft: '10px' }}>
            Recalibrate
          </button>
          <button onClick={() => navigate('/scoreadmin')} style={{ ...buttonStyle('#9C27B0'), padding: '4px 12px', fontSize: '12px', margin: 0, marginLeft: '10px' }}>
            Score Admin
          </button>
        </div>

        {/* Event Log - Below Video */}
        <div style={{
          marginTop: '10px',
        }}>
          <h3 style={{ textAlign: 'center', marginBottom: '10px' }}>Event Log</h3>
          <div
            style={{
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid #ccc',
              padding: '10px',
              borderRadius: '4px',
              backgroundColor: '#f9f9f9',
            }}
          >
            {events.length === 0 ? (
              <p style={{ color: '#999', textAlign: 'center' }}>No events yet. Start detection to begin scoring.</p>
            ) : (
              events.map(event => (
                <div
                  key={event.id}
                  style={{
                    padding: '8px',
                    marginBottom: '5px',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    borderLeft: '4px solid #4CAF50',
                  }}
                >
                  <strong>+{event.holeValue} points</strong> at{' '}
                  {new Date(event.timestamp).toLocaleTimeString()}
                  <br />
                  <small style={{ color: '#666' }}>
                    Position: ({Math.round(event.x)}, {Math.round(event.y)}) | Confidence:{' '}
                    {(event.confidence * 100).toFixed(0)}%
                  </small>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function buttonStyle(bgColor: string): React.CSSProperties {
  return {
    margin: '0 5px',
    padding: '10px 20px',
    backgroundColor: bgColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
  };
}

